import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Select,
  Textarea,
  Input,
  Group,
  Checkbox,
  NumberInput,
} from "@mantine/core";
import NODE_TYPES from "./nodeTypes";
import OrderList from "../orderList";
import ContactInfoRow from "../contactInfoRow";
import { IconPlus, IconTrash } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";

const WorkFlowNodeSetting = ({ form, opened, onClose, node, onSave }) => {
  const [formData, setFormData] = useState({});
  const workflowId = _.get(form.values, "_id");
  const [api] = useServerApi();
  const [credentials, setCredentials] = useState([]);

  const fetchCredentials = async () => {
    const credentials = await api.AIWorkFlow.getCredentials(workflowId);
    setCredentials(credentials);
  };

  useEffect(() => {
    setFormData(node?.data || {});
    fetchCredentials();
  }, [node]);

  if (!node) return null;

  // Get the node type definition
  const nodeTypeDef = NODE_TYPES.find((nt) => nt.type === node.type);

  // console.log("nodeTypeDef", node.type?.toUpperCase(), nodeTypeDef, node);
  const fields = nodeTypeDef?.fields || [];

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave({ ...node, data: { ...node.data, ...formData } });
    }
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Node: ${formData.label || nodeTypeDef?.label || "Node"}`}
      style={{ marginTop: 40 }}
      size="xl"
    >
      <Stack>
        {fields.map((field) => {
          if (
            field.showOnlyIf &&
            !field.showOnlyIf({ ...node, data: formData })
          ) {
            return null;
          }
          if (field.type === "text") {
            return (
              <TextInput
                key={field.name}
                label={field.label}
                placeholder={field.placeholder}
                value={formData[field.name] || ""}
                size="xs"
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
              />
            );
          }
          if (field.type === "select") {
            return (
              <Select
                key={field.name}
                label={field.label}
                data={field.options.map((opt) => ({ label: opt, value: opt }))}
                value={formData[field.name] || ""}
                onChange={(value) => handleFieldChange(field.name, value)}
                size="xs"
              />
            );
          }
          if (field.type === "number") {
            return (
              <NumberInput
                key={field.name}
                label={field.label}
                placeholder={field.placeholder}
                value={formData[field.name] || ""}
                onChange={(value) => handleFieldChange(field.name, value)}
                size="xs"
              />
            );
          }
          if (field.type === "textarea") {
            return (
              <Textarea
                key={field.name}
                label={field.label}
                placeholder={field.placeholder}
                value={formData[field.name] || ""}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                autosize
                size="xs"
                defaultValue={field.defaultValue}
                minRows={10}
                maxRows={50}
              />
            );
          }

          if (field.type === "checkbox") {
            return (
              <Checkbox
                key={field.name}
                label={field.label}
                checked={formData[field.name] || false}
                type="checkbox"
                size="xs"
                onChange={(e) =>
                  handleFieldChange(field.name, e.target.checked)
                }
              />
            );
          }
          if (field.type === "credentialSelect") {
            return (
              <Select
                key={field.name}
                label={field.label}
                data={credentials.map((cred) => ({
                  label: cred.name,
                  value: cred._id,
                }))}
                value={formData[field.name] || ""}
                onChange={(value) => handleFieldChange(field.name, value)}
                size="xs"
              />
            );
          }

          if (field.type === "fieldList") {
            return (
              <>
                <Group>
                  <Input.Label size="xs">{field.label}</Input.Label>
                  <Button
                    onClick={() =>
                      handleFieldChange(field.name, [
                        ...(formData[field.name] || []),
                        {
                          field: field.options[0]?.field,
                          value: field.options[0]?.defaultValue ?? "",
                        },
                      ])
                    }
                    size="xs"
                    variant="subtle"
                    leftIcon={<IconPlus size={14} />}
                  >
                    Add
                  </Button>
                </Group>
                {formData[field.name]?.map((item, index) => {
                  const option =
                    field.options.find((opt) => opt.field === item.field) ||
                    field.options[0];
                  let valueInput = null;
                  if (option.fieldType === "text") {
                    valueInput = (
                      <TextInput
                        placeholder={option.placeholder ?? option.field}
                        value={item.value}
                        size="xs"
                        onChange={(e) => {
                          const newArr = [...formData[field.name]];
                          newArr[index] = {
                            ...newArr[index],
                            value: e.target.value,
                          };
                          handleFieldChange(field.name, newArr);
                        }}
                        style={{ flex: 1 }}
                      />
                    );
                  } else if (option.fieldType === "checkbox") {
                    valueInput = (
                      <Checkbox
                        checked={!!item.value}
                        size="xs"
                        onChange={(e) => {
                          const newArr = [...formData[field.name]];
                          newArr[index] = {
                            ...newArr[index],
                            value: e.target.checked,
                          };
                          handleFieldChange(field.name, newArr);
                        }}
                        style={{ flex: 1 }}
                        label={option.field}
                      />
                    );
                  } else if (option.fieldType === "number") {
                    valueInput = (
                      <NumberInput
                        type="number"
                        placeholder={option.placeholder ?? option.field}
                        value={item.value}
                        size="xs"
                        min={option.min}
                        max={option.max}
                        onChange={(e) => {
                          const newArr = [...formData[field.name]];
                          newArr[index] = {
                            ...newArr[index],
                            value: e.target.value,
                          };
                          handleFieldChange(field.name, newArr);
                        }}
                        style={{ flex: 1 }}
                      />
                    );
                  } else if (option.fieldType === "select") {
                    valueInput = (
                      <Select
                        placeholder={option.field}
                        value={item.value}
                        size="xs"
                        onChange={(value) => {
                          const newArr = [...formData[field.name]];
                          newArr[index] = {
                            ...newArr[index],
                            value: value,
                          };
                          handleFieldChange(field.name, newArr);
                        }}
                        style={{ flex: 1 }}
                        data={
                          option.options?.map((opt) => ({
                            label: opt,
                            value: opt,
                          })) || []
                        }
                      />
                    );
                  }

                  return (
                    <Group
                      position="apart"
                      style={{ width: "100%" }}
                      key={index}
                    >
                      <Group style={{ flex: 1 }}>
                        <Select
                          placeholder={"Field"}
                          value={item.field}
                          data={field.options?.map((opt) => ({
                            label: opt.field,
                            value: opt.field,
                          }))}
                          size="xs"
                          onChange={(value) => {
                            const opt =
                              field.options.find((o) => o.field === value) ||
                              {};
                            const newArr = [...formData[field.name]];
                            newArr[index] = {
                              field: value,
                              value: opt.defaultValue ?? "",
                            };
                            handleFieldChange(field.name, newArr);
                          }}
                          style={{ flex: 1 }}
                        />
                        {valueInput}
                      </Group>
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                          const newItems = formData[field.name].filter(
                            (_, i) => i !== index
                          );
                          handleFieldChange(field.name, newItems);
                        }}
                      >
                        <IconTrash size={14} />
                      </Button>
                    </Group>
                  );
                })}
              </>
            );
          }

          // Add more field types as needed
          return null;
        })}
        <Button onClick={handleSave} mt="xl">
          Save
        </Button>
      </Stack>
    </Modal>
  );
};

export default WorkFlowNodeSetting;
